import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import _defineProperty from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { RA$ } from '@infl-ts/util-fp-ts';
import { memoize as memo } from '@infl-ts/util-function';
import { makeDecodeError } from '@infl-ts/util-make-apollo-decoded';
import { resolveHubApiError } from '@infl-ts/util-query-errors';
import axios from 'axios';
import * as E from 'fp-ts/Either';
import { constVoid, flow, identity, pipe } from 'fp-ts/function';
import * as TE from 'fp-ts/TaskEither';
import * as t from 'io-ts';
import * as L from 'monocle-ts/Lens';
import { StreamChat } from 'stream-chat';
import { HUB_API_URL, STREAM_IO_API_KEY } from '../env';
var chatClient = StreamChat.getInstance(STREAM_IO_API_KEY, {
  timeout: 6000
});
var jwtTokenLens = pipe(L.id(), L.prop('headers'), L.prop('X_JWT_TOKEN'));
var orgIdLens = pipe(L.id(), L.prop('headers'), L.prop('X_ORG_ID'));
var acceptLanguageLens = pipe(L.id(), L.prop('headers'), L.prop('Accept-Language'));

function defaultToObject(x) {
  return typeof x === 'object' && x != null && !RA$.isArray(x) ? x : {};
}

var setDefaultConfig = function setDefaultConfig(config) {
  return _objectSpread(_objectSpread({}, config), {}, {
    headers: _objectSpread({
      X_JWT_TOKEN: '',
      X_ORG_ID: '',
      'Accept-Language': ''
    }, defaultToObject(config.headers))
  });
};

var memoTryCatch = function memoTryCatch(f, onRejected) {
  return pipe(memo(f, {
    promise: true
  }), function (mem) {
    return Object.assign(function () {
      return mem().then(E.right, function (reason) {
        return E.left(onRejected(reason));
      });
    }, {
      "delete": mem["delete"],
      clear: mem.clear
    });
  });
};

export var doMemoAxiosReq = function doMemoAxiosReq(config) {
  return memoTryCatch(function () {
    return axios.request(config);
  }, resolveHubApiError);
};
export var doAxiosReq = function doAxiosReq(config) {
  return TE.tryCatch(function () {
    return axios.request(config);
  }, resolveHubApiError);
}; // Keyed by baseURL: on an embedded third-party page the document origin is
// the customer's site, not the hub, so an origin-relative POST /token 404s
// there (ka#60472436). baseURL must travel with the request the same way
// hubConfigTE/getWindowObjTE already carry theirs, and keying the memo by it
// keeps same-origin hub pages (baseURL undefined) and embedded pages for
// different hubs from ever sharing a cached JWT.

var getMemoTokenTE = memo(function (baseURL) {
  return doMemoAxiosReq({
    method: 'POST',
    url: '/token',
    baseURL: baseURL,
    data: null,
    withCredentials: true,
    headers: {
      Accept: 'application/jwt'
    }
  });
}, {
  primitive: true
});

var destroyJwtToken = function destroyJwtToken() {
  return void getMemoTokenTE.clear();
};

var getTokenTE = function getTokenTE(baseURL) {
  return pipe(getMemoTokenTE(baseURL), TE.map(function (x) {
    return x.data;
  }), TE.chainEitherKW(function (data) {
    return pipe(t.string.decode(data), E.mapLeft(makeDecodeError));
  }));
};

export var HubConfig = t.type({
  data: t.type({
    hub: t.type({
      id: t.string
    })
  })
});

var hubConfigTE = function hubConfigTE(jwt, baseURL) {
  return pipe(doMemoAxiosReq({
    method: 'GET',
    url: '/api/company_config',
    baseURL: baseURL,
    withCredentials: true,
    headers: jwt ? {
      Accept: 'application/jwt',
      Authorization: "Bearer ".concat(jwt)
    } : {
      Accept: 'application/jwt'
    }
  }), TE.map(function (x) {
    return x.data;
  }), TE.chainEitherKW(flow(function (x) {
    return HubConfig.decode(x);
  }, E.mapLeft(makeDecodeError))));
};

export var WindowObj = t.type({
  hub: t.type({
    lang: t.string
  })
});
var getWindowObjTE = memo(function (headers) {
  return pipe(doMemoAxiosReq({
    method: 'GET',
    url: '/api/c2_homepage/infl_window_object',
    withCredentials: true,
    baseURL: HUB_API_URL,
    headers: headers
  }), TE.map(function (x) {
    return x.data;
  }), TE.chainEitherKW(flow(function (x) {
    return WindowObj.decode(x);
  }, E.mapLeft(makeDecodeError))));
}, {
  primitive: true,
  normalizer: function normalizer(_ref) {
    var _ref2 = _slicedToArray(_ref, 1),
        _ref2$ = _ref2[0],
        X_JWT_TOKEN = _ref2$.X_JWT_TOKEN,
        X_ORG_ID = _ref2$.X_ORG_ID;

    return X_JWT_TOKEN + X_ORG_ID;
  }
});
export var HubJwtToken = t.type({
  access_token: t.string
});
export var fetchHubJwtToken = function fetchHubJwtToken(path) {
  return pipe(doAxiosReq({
    method: 'POST',
    url: path,
    withCredentials: true
  }), TE.map(function (x) {
    return x.data;
  }), TE.chainEitherKW(flow(function (x) {
    return HubJwtToken.decode(x);
  }, E.mapLeft(makeDecodeError))), TE.map(function (x) {
    return x.access_token;
  }));
};

var getAndSetHubConfig = function getAndSetHubConfig(config, jwt, currentHubURL) {
  return pipe(TE.Do, TE.apSW('X_JWT_TOKEN', jwt ? TE.of(jwt) : getTokenTE(currentHubURL)), TE.apSW('X_ORG_ID', pipe(hubConfigTE(jwt, currentHubURL), TE.map(function (result) {
    return result.data.hub.id;
  }))), TE.map(function (_ref3) {
    var X_ORG_ID = _ref3.X_ORG_ID,
        X_JWT_TOKEN = _ref3.X_JWT_TOKEN;
    return pipe(config, jwtTokenLens.set(X_JWT_TOKEN), orgIdLens.set(X_ORG_ID));
  }));
};

var getAndSetHubLang = function getAndSetHubLang(config) {
  return pipe(getWindowObjTE(config.headers), TE.map(function (result) {
    return pipe(config, acceptLanguageLens.set(result.hub.lang));
  }));
};

export var logout = function logout() {
  var destroyToken = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
  if (chatClient) void chatClient.disconnectUser();
  if (destroyToken) destroyJwtToken();
  var signOutUrl = '/users/sign_out';
  if (true) window.location.assign("https://".concat(window.location.hostname).concat(signOutUrl));
  return signOutUrl;
};

var retryWithNewToken = function retryWithNewToken(config, currentHubURL) {
  return pipe(TE.rightIO(destroyJwtToken), TE.chain(function () {
    return getTokenTE(currentHubURL);
  }), TE.map(function (X_JWT_TOKEN) {
    return pipe(config, jwtTokenLens.set(X_JWT_TOKEN));
  }), TE.chainW(doAxiosReq));
};

export var hasUnauthorizedCreds = function hasUnauthorizedCreds(response) {
  return 401 === response.status;
};

var hasInvalidToken = function hasInvalidToken(response) {
  return 498 === response.status;
};

var makeRetryOnError = function makeRetryOnError(shouldLogOut, currentHubURL) {
  return function go(error) {
    var count = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 2;

    if (error._tag === 'axios' && error.error.response) {
      if (hasUnauthorizedCreds(error.error.response)) {
        return pipe(shouldLogOut ? function () {
          return logout();
        } : constVoid, TE.rightIO, TE.chain(function () {
          return TE.throwError(error);
        }));
      }

      if (hasInvalidToken(error.error.response)) {
        return count <= 0 ? pipe(TE.rightIO(logout), TE.chain(function () {
          return TE.throwError(error);
        })) : pipe(error.error.response.config, setDefaultConfig, function (config) {
          return retryWithNewToken(config, currentHubURL);
        }, TE.orElse(function (e) {
          return go(e, count - 1);
        }));
      }
    }

    return TE.throwError(error);
  };
};

var hubApi = function hubApi() {
  var _ref4 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref4$shouldLogOut = _ref4.shouldLogOut,
      shouldLogOut = _ref4$shouldLogOut === void 0 ? true : _ref4$shouldLogOut,
      _ref4$axiosInstance = _ref4.axiosInstance,
      axiosInstance = _ref4$axiosInstance === void 0 ? axios.create({
    withCredentials: true,
    baseURL: HUB_API_URL
  }) : _ref4$axiosInstance,
      jwt = _ref4.jwt,
      currentHubURL = _ref4.currentHubURL;

  axiosInstance.interceptors.request.use(flow(setDefaultConfig, function (config) {
    return getAndSetHubConfig(config, jwt, currentHubURL);
  }, TE.chain(getAndSetHubLang), TE.getOrElse(function (err) {
    return function () {
      return Promise.reject(err);
    };
  }), function (res) {
    return res();
  }), function (err) {
    return Promise.reject(resolveHubApiError(err));
  });
  axiosInstance.interceptors.response.use(identity, flow(resolveHubApiError, makeRetryOnError(shouldLogOut, currentHubURL), TE.getOrElse(function (err) {
    return function () {
      return Promise.reject(err);
    };
  }), function (res) {
    return res();
  }));
  return {
    destroyJwtToken: destroyJwtToken,
    request: function request(config) {
      return TE.tryCatch(function () {
        return axiosInstance.request(config);
      }, function (err) {
        return err;
      });
    }
  };
};

export var api = hubApi();
export { getTokenTE, hubApi, hubConfigTE };